import { useRef } from 'react';
import classNames from 'classnames';

import lang from '../../../lib/language';

import walletImage from '../../../assets/bigImages/referrals-payout-wallet.png';
import { GlobalClassesEnum } from '../../../assets/scss/globalClasses/globalClasses.constants';
import { PayoutModalIconEnum, PayoutRequestStateEnum } from '../../../containers/Referrals/Referrals.types';
import { useIsMobile } from '../../../hooks';
import BaseModal from '../../../shared_components/BaseModal/BaseModal';
import MobileDrawer from '../../../shared_components/MobileDrawer/MobileDrawer';
import { getPayoutModalContent } from '../helpers';

import styles from './ReferralPayoutModal.module.scss';

type Props = {
  state: PayoutRequestStateEnum;
  amountLabel: string;
  onConfirm: () => void;
  onClose: () => void;
};

const icons: Record<PayoutModalIconEnum, JSX.Element> = {
  [PayoutModalIconEnum.error]: <div className={styles.errorIcon} />,
  [PayoutModalIconEnum.success]: <div className={styles.successIcon} />,
  [PayoutModalIconEnum.wallet]: <img className={styles.walletIcon} src={walletImage} alt="" />,
};

const descriptionClasses = classNames(
  GlobalClassesEnum.CommonBody1Text,
  GlobalClassesEnum.CommonTextSecondary,
  styles.description,
);

const ReferralPayoutModal = ({ state, amountLabel, onConfirm, onClose }: Props) => {
  const { isMobile } = useIsMobile();
  const lastVisibleState = useRef(state);

  if (state !== PayoutRequestStateEnum.closed) {
    lastVisibleState.current = state;
  }

  const { description, icon, isConfirmStep, primaryLabel, title } = getPayoutModalContent(
    lastVisibleState.current,
    amountLabel,
  );

  const body = (
    <div className={styles.body}>
      {icons[icon]}
      <div className={GlobalClassesEnum.CommonH3Bold}>{title}</div>
      <div className={descriptionClasses}>
        {description}
      </div>
      <div className={styles.actions}>
        <button type="button" className={styles.primaryButton} onClick={isConfirmStep ? onConfirm : onClose}>
          {primaryLabel}
        </button>
        {isConfirmStep && (
          <button type="button" className={styles.secondaryButton} onClick={onClose}>
            {lang.referralsPayoutCancel()}
          </button>
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <MobileDrawer
        isOpen={state !== PayoutRequestStateEnum.closed}
        title={title}
        showTitle={false}
        showCloseButton={false}
        onClose={onClose}
      >
        {body}
      </MobileDrawer>
    );
  }

  return (
    <BaseModal isOpen={state !== PayoutRequestStateEnum.closed} toggle={onClose}>
      {body}
    </BaseModal>
  );
};

export default ReferralPayoutModal;
